import type { DeliveryAddress } from '../../services/deliveryService';
import {
  googleMapsNs,
  loadGoogleMaps,
  placesHost,
  type GoogleAddressComponent,
  type GoogleGeocodeResult,
  type GooglePlaceDetails,
  type GooglePrediction,
} from './googleMaps';

let sessionToken: unknown = null;

function sessionForPlaces(): unknown {
  const google = googleMapsNs();
  if (!sessionToken && google?.maps?.places?.AutocompleteSessionToken) {
    sessionToken = new google.maps.places.AutocompleteSessionToken();
  }
  return sessionToken;
}

function resetPlacesSession() {
  sessionToken = null;
}

export type GeocodedPlace = {
  lat: number;
  lon: number;
  label: string;
  area?: string;
  locality?: string;
  city?: string;
  state?: string;
  pincode?: string;
  country?: string;
  placeId?: string;
  geocodeStatus: string;
};

export type PlaceSuggestion = {
  placeId: string;
  label: string;
  secondary?: string;
};

function componentOf(components: GoogleAddressComponent[] | undefined, type: string): string {
  if (!components) {
    return '';
  }
  const match = components.find((item) => item.types && item.types.indexOf(type) !== -1);
  return match ? match.long_name : '';
}

function firstComponent(results: GoogleGeocodeResult[], type: string): string {
  for (let i = 0; i < results.length; i += 1) {
    const value = componentOf(results[i].address_components, type);
    if (value) {
      return value;
    }
  }
  return '';
}

function fromResults(results: GoogleGeocodeResult[]): Omit<GeocodedPlace, 'lat' | 'lon' | 'label'> {
  const primary = fromComponents(results[0]?.address_components);
  const city =
    firstComponent(results, 'locality') ||
    firstComponent(results, 'administrative_area_level_3') ||
    firstComponent(results, 'administrative_area_level_2') ||
    primary.city;
  return {
    area: primary.area || firstComponent(results, 'route'),
    locality:
      primary.locality ||
      firstComponent(results, 'sublocality_level_1') ||
      firstComponent(results, 'sublocality') ||
      firstComponent(results, 'neighborhood'),
    city,
    state: firstComponent(results, 'administrative_area_level_1') || primary.state,
    pincode: firstComponent(results, 'postal_code') || primary.pincode,
    country: firstComponent(results, 'country') || primary.country,
    geocodeStatus: 'RESOLVED',
  };
}

function fromComponents(components: GoogleAddressComponent[] | undefined): Omit<GeocodedPlace, 'lat' | 'lon' | 'label'> {
  const streetNumber = componentOf(components, 'street_number');
  const route = componentOf(components, 'route');
  const area = [streetNumber, route].filter(Boolean).join(' ');
  const locality =
    componentOf(components, 'sublocality_level_1') ||
    componentOf(components, 'sublocality') ||
    componentOf(components, 'neighborhood');
  const city =
    componentOf(components, 'locality') ||
    componentOf(components, 'administrative_area_level_3') ||
    componentOf(components, 'administrative_area_level_2');
  return {
    area,
    locality,
    city,
    state: componentOf(components, 'administrative_area_level_1'),
    pincode: componentOf(components, 'postal_code'),
    country: componentOf(components, 'country'),
    geocodeStatus: components && components.length ? 'RESOLVED' : 'PIN_ONLY',
  };
}

function pinFallback(lat: number, lon: number): GeocodedPlace {
  return {
    lat,
    lon,
    label: `${lat.toFixed(5)}, ${lon.toFixed(5)}`,
    geocodeStatus: 'PIN_ONLY',
  };
}

export function geocodedFromGooglePlace(place: GooglePlaceDetails): GeocodedPlace | null {
  if (!place?.geometry?.location) {
    return null;
  }
  const lat = place.geometry.location.lat();
  const lon = place.geometry.location.lng();
  return {
    lat,
    lon,
    label: place.formatted_address || place.name || pinFallback(lat, lon).label,
    placeId: place.place_id,
    ...fromComponents(place.address_components),
    geocodeStatus: 'RESOLVED',
  };
}

export async function reverseGeocode(apiKey: string, lat: number, lon: number): Promise<GeocodedPlace> {
  const fallback = pinFallback(lat, lon);
  try {
    const google = await loadGoogleMaps(apiKey);
    const geocoder = new google.maps.Geocoder();
    return await new Promise((resolve) => {
      geocoder.geocode({ location: { lat, lng: lon } }, (results: GoogleGeocodeResult[] | null, status: string) => {
        if (status !== 'OK' || !results || !results.length) {
          resolve(fallback);
          return;
        }
        const first = results[0];
        resolve({
          lat,
          lon,
          label: first.formatted_address || fallback.label,
          placeId: first.place_id,
          ...fromResults(results),
        });
      });
    });
  } catch {
    return fallback;
  }
}

export async function searchPlaces(
  apiKey: string,
  query: string,
  bias?: { lat: number; lng: number }
): Promise<PlaceSuggestion[]> {
  const q = query.trim();
  if (q.length < 3) {
    return [];
  }
  const google = await loadGoogleMaps(apiKey);
  const service = new google.maps.places.AutocompleteService();
  const token = sessionForPlaces();
  return await new Promise((resolve) => {
    service.getPlacePredictions(
      {
        input: q,
        componentRestrictions: { country: 'in' },
        ...(token ? { sessionToken: token } : {}),
        ...(bias && Number.isFinite(bias.lat) && Number.isFinite(bias.lng)
          ? { locationBias: { radius: 50000, center: { lat: bias.lat, lng: bias.lng } } }
          : {}),
      },
      (predictions: GooglePrediction[] | null, status: string) => {
        if (status !== 'OK' || !predictions) {
          resolve([]);
          return;
        }
        resolve(
          predictions.map((row) => ({
            placeId: row.place_id,
            label: row.structured_formatting?.main_text || row.description,
            secondary: row.structured_formatting?.secondary_text || row.description,
          }))
        );
      }
    );
  });
}

export async function placeDetails(apiKey: string, placeId: string): Promise<GeocodedPlace | null> {
  const google = await loadGoogleMaps(apiKey);
  const service = new google.maps.places.PlacesService(placesHost());
  return await new Promise((resolve) => {
    service.getDetails(
      {
        placeId,
        fields: ['geometry', 'formatted_address', 'address_components', 'name', 'place_id'],
        ...(sessionForPlaces() ? { sessionToken: sessionForPlaces() } : {}),
      },
      (place: GooglePlaceDetails | null, status: string) => {
        resetPlacesSession();
        if (status !== 'OK' || !place?.geometry?.location) {
          resolve(null);
          return;
        }
        const lat = place.geometry.location.lat();
        const lon = place.geometry.location.lng();
        resolve({
          lat,
          lon,
          label: place.formatted_address || place.name || pinFallback(lat, lon).label,
          placeId: place.place_id || placeId,
          ...fromComponents(place.address_components),
          geocodeStatus: 'RESOLVED',
        });
      }
    );
  });
}

export function buildingDetailsOf(address: DeliveryAddress): string {
  return [address.floor, address.tower, address.building].filter(Boolean).join(', ');
}

export function streetLocalityOf(address: DeliveryAddress): string {
  const seen: string[] = [];
  [address.area, address.locality].forEach((value) => {
    if (value && seen.indexOf(value) === -1) {
      seen.push(value);
    }
  });
  return seen.join(', ');
}
